import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2, XCircle, Clock, GitPullRequest, Rocket,
  Stethoscope, Lightbulb, FileDiff, TrendingUp,
} from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type Proposal = Database["public"]["Tables"]["sentinel_proposals"]["Row"];

const STATE_COLORS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "outline", candidate: "secondary", pending: "default",
  approved: "default", applied: "default", reverted: "destructive",
};

const RISK_COLORS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  low: "outline", medium: "secondary", high: "default", critical: "destructive",
};

interface ProposalDiffProps {
  proposal: Proposal;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onApply?: (id: string) => void;
  isApplying?: boolean;
}

export function ProposalDiff({ proposal, onApprove, onReject, onApply, isApplying }: ProposalDiffProps) {
  const diff = proposal.diff as Record<string, unknown> | null;
  const impact = proposal.impact_estimate as Record<string, unknown> | null;
  // Onda 9a: canary_config foi removido. Aplicação de propostas é binária.
  const canProgress = proposal.state === "pending" || proposal.state === "approved";
  const risk = (proposal as unknown as { risk_level?: string }).risk_level || "medium";
  const diagnosis = (proposal as unknown as { diagnosis?: string }).diagnosis;
  const hypothesis = (proposal as unknown as { hypothesis?: string }).hypothesis;
  const hasDiff = diff && Object.keys(diff).length > 0;
  const hasImpact = impact && Object.keys(impact).length > 0;

  return (
    <Card className="hover:shadow-md transition-shadow overflow-hidden">
      {/* Block 1 — Header */}
      <CardHeader className="pb-3 bg-muted/30 border-b">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <GitPullRequest className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <h4 className="text-sm font-semibold truncate">{proposal.title || "Sem título"}</h4>
              <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                <Badge variant="outline" className="text-[10px] py-0">{proposal.category}</Badge>
                <span>•</span>
                <span>{new Date(proposal.created_at).toLocaleDateString("pt-BR")}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end shrink-0">
            <Badge variant={RISK_COLORS[risk] || "outline"} className="text-[10px]">risco: {risk}</Badge>
            <Badge variant={STATE_COLORS[proposal.state] || "outline"} className="text-[10px]">{proposal.state}</Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-3">
        {/* Block 2 — Diagnóstico */}
        {(diagnosis || (!hypothesis && proposal.description)) && (
          <section>
            <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              <Stethoscope className="w-3 h-3" aria-hidden="true" />
              Diagnóstico
            </div>
            <p className="text-xs leading-relaxed text-foreground">
              {diagnosis || proposal.description}
            </p>
          </section>
        )}

        {/* Block 3 — Hipótese */}
        {hypothesis && (
          <section>
            <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              <Lightbulb className="w-3 h-3" aria-hidden="true" />
              Hipótese
            </div>
            <p className="text-xs leading-relaxed text-foreground">{hypothesis}</p>
          </section>
        )}

        {/* Block 4 — Diff */}
        {hasDiff && (
          <section>
            <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              <FileDiff className="w-3 h-3" aria-hidden="true" />
              Mudança proposta
            </div>
            <pre className="text-[10px] bg-muted/50 border rounded p-2 overflow-x-auto max-h-28 leading-relaxed">
              {JSON.stringify(diff, null, 2)}
            </pre>
          </section>
        )}

        {/* Block 5 — Impacto */}
        {hasImpact && (
          <section>
            <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              <TrendingUp className="w-3 h-3" aria-hidden="true" />
              Impacto estimado
            </div>
            <ul className="text-xs space-y-0.5 text-foreground">
              {Object.entries(impact).map(([k, v]) => (
                <li key={k} className="flex gap-2">
                  <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}:</span>
                  <span className="font-medium">{String(v)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Onda 9a: indicador de canary removido — aplicação é binária. */}
        {canProgress && (
          <div className="space-y-1 pt-1 border-t">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>Aplicação binária — sem rollout gradual.</span>
            </div>
            <Progress value={proposal.state === "applied" ? 100 : proposal.state === "approved" ? 50 : 10} className="h-1.5" />
          </div>
        )}

        {/* Actions */}
        {(proposal.state === "pending" || proposal.state === "candidate") && (
          <div className="flex gap-2 pt-2 border-t">
            <Button size="sm" variant="default" onClick={() => onApprove?.(proposal.id)} className="flex-1">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Aprovar
            </Button>
            <Button size="sm" variant="outline" onClick={() => onReject?.(proposal.id)} className="flex-1">
              <XCircle className="w-3 h-3 mr-1" /> Rejeitar
            </Button>
          </div>
        )}

        {proposal.state === "approved" && onApply && (
          <Button size="sm" variant="default" onClick={() => onApply(proposal.id)} disabled={isApplying} className="w-full mt-1">
            <Rocket className="w-3 h-3 mr-1" /> {isApplying ? "Aplicando..." : "Aplicar (snapshot)"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
